// Charts for the desk (decision 84): plain SVG and HTML, no library. The rules they follow: one axis, thin marks with a
// 4px rounded data end, hairline grid, a legend for two series or more, values at the bar tips, a tooltip on hover and
// keyboard focus, and the numbers in a table beside every chart for screen readers. The colors are the --viz-*
// tokens in styles.css, validated for color-blind separation on the card surface.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** The width of an element, kept current as the window changes; 640 until measured (and in tests). */
export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = Math.round(el.getBoundingClientRect().width);
      if (next > 0) setW(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Clean axis ticks from 0 to at least max: steps of 1, 2, 2.5 or 5 times a power of ten. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const out: number[] = [];
  for (let v = 0; v < max + step * 0.999; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out.length < 2 ? [0, step] : out;
}

/** A compact money or count for an axis: $1.2K, $35K, $1.4M. */
export function compact(v: number, intlLocale: string, money = true): string {
  return new Intl.NumberFormat(intlLocale, { notation: 'compact', maximumFractionDigits: 1, ...(money ? { style: 'currency', currency: 'USD' } : {}) }).format(v);
}

/** A bar's path: square at the baseline, 4px rounded at its data end (top for a column). */
function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  if (h <= 0) return '';
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

export type TrendSeries = { name: string; values: (number | null)[]; color: string };
export type TrendPoint = { key: string; axis: string; title: string; faded?: boolean };

/**
 * A time trend: columns for the main series and, when given, a 2px line with dots for a comparison in the same unit
 * (last year), or a line alone (a percent). One y-axis from zero, hairline grid, the latest value labeled.
 */
export function TrendChart({
  label, points, bar, line, format, axisFormat, height = 220, fadedNote, legend = true,
}: {
  label: string;
  points: TrendPoint[];
  bar?: TrendSeries;
  line?: TrendSeries;
  format: (v: number) => string;
  axisFormat: (v: number) => string;
  height?: number;
  fadedNote?: string;
  legend?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = points.length;
  const all = [...(bar?.values ?? []), ...(line?.values ?? [])].filter((v): v is number => v !== null);
  const posTicks = niceTicks(Math.max(0, ...all));
  const minV = Math.min(0, ...all);
  const step = (posTicks[1] ?? 1) - (posTicks[0] ?? 0);
  // below zero (a week sold under cost) the axis steps down by the same amount
  const ticks = [...Array.from({ length: minV < 0 ? Math.ceil(-minV / step) : 0 }, (_, i) => -(i + 1) * step).reverse(), ...posTicks];
  const top = ticks[ticks.length - 1]!;
  const bottom = ticks[0]!;
  const padL = Math.max(36, ...ticks.map((t) => axisFormat(t).length * 7 + 8));
  const padR = 12;
  const padT = 14;
  const axisH = 22;
  const plotW = Math.max(40, width - padL - padR);
  const plotH = height - padT - axisH;
  const band = n ? plotW / n : plotW;
  const barW = Math.max(2, Math.min(24, band * 0.62));
  const y = (v: number) => padT + plotH - (top > bottom ? ((v - bottom) / (top - bottom)) * plotH : 0);
  const cx = (i: number) => padL + band * i + band / 2;
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 64))));
  const lastBar = bar ? bar.values.length - 1 : -1;
  const linePath = line
    ? line.values.reduce<string>((d, v, i) => (v === null ? d : `${d}${d && line.values[i - 1] != null ? 'L' : 'M'}${cx(i).toFixed(1)},${y(v).toFixed(1)}`), '')
    : '';
  // the tooltip opens away from the nearer edge, so it never sticks out of the card
  const tipX = hover === null ? 0 : cx(hover);
  const tipAlign = hover === null ? 'mid' : tipX < width / 3 ? 'left' : tipX > (2 * width) / 3 ? 'right' : 'mid';
  const step1 = (d: number) => setHover((h) => (n ? Math.min(n - 1, Math.max(0, (h ?? n - 1) + d)) : null));

  return (
    <figure className="viz" aria-label={label}>
      {legend && bar && line ? (
        <div className="viz-legend" aria-hidden="true">
          <span><i className="key key-bar" style={{ background: bar.color }} /> {bar.name}</span>
          <span><i className="key key-line" style={{ background: line.color }} /> {line.name}</span>
          {fadedNote && points.some((p) => p.faded) ? <span className="muted">{fadedNote}</span> : null}
        </div>
      ) : null}
      <div
        ref={ref}
        className="viz-plot"
        tabIndex={n ? 0 : -1}
        aria-label={label}
        onMouseLeave={() => setHover(null)}
        onFocus={() => setHover((h) => h ?? (n ? n - 1 : null))}
        onBlur={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); step1(-1); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); step1(1); }
          else if (e.key === 'Home') { e.preventDefault(); setHover(0); }
          else if (e.key === 'End') { e.preventDefault(); setHover(n - 1); }
        }}
      >
        <svg width={width} height={height} aria-hidden="true">
          {ticks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} className={t === 0 ? 'viz-base' : 'viz-grid'} />
              <text x={padL - 6} y={y(t)} dy="0.32em" textAnchor="end" className="viz-tick">{axisFormat(t)}</text>
            </g>
          ))}
          {bar
            ? bar.values.map((v, i) =>
                v === null || v <= 0 ? null : (
                  <path key={points[i]!.key} d={columnPath(cx(i) - barW / 2, y(v), barW, y(0) - y(v))} fill={bar.color} opacity={points[i]!.faded ? 0.45 : hover === null || hover === i ? 1 : 0.7} />
                ),
              )
            : null}
          {line ? <path d={linePath} fill="none" stroke={line.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" /> : null}
          {line
            ? line.values.map((v, i) =>
                v === null || (n > 26 && hover !== i && i !== n - 1) ? null : (
                  <circle key={points[i]!.key} cx={cx(i)} cy={y(v)} r={4} fill={line.color} className="viz-dot" />
                ),
              )
            : null}
          {/* every k-th label, and the last one, never one so close to it that the two collide */}
          {points.map((p, i) =>
            i === n - 1 || (i % every === 0 && n - 1 - i >= every) ? (
              <text key={p.key} x={cx(i)} y={height - 6} textAnchor={i === n - 1 && n > 1 ? 'end' : 'middle'} dx={i === n - 1 && n > 1 ? band / 2 : 0} className="viz-tick">
                {p.axis}
              </text>
            ) : null,
          )}
          {/* the latest column's value at its cap */}
          {bar && lastBar >= 0 && bar.values[lastBar] ? (
            <text x={cx(lastBar)} y={y(bar.values[lastBar]!) - 5} textAnchor="middle" className="viz-value">{format(bar.values[lastBar]!)}</text>
          ) : null}
          {!bar && line && n ? (() => {
            const last = [...line.values].reverse().findIndex((v) => v !== null);
            if (last < 0) return null;
            const i = n - 1 - last;
            return <text x={cx(i)} y={y(line.values[i]!) - 9} textAnchor="end" className="viz-value">{format(line.values[i]!)}</text>;
          })() : null}
          {points.map((p, i) => (
            <rect
              key={p.key}
              x={padL + band * i}
              y={padT}
              width={band}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              className="viz-hit"
            />
          ))}
          {hover !== null ? <line x1={cx(hover)} x2={cx(hover)} y1={padT} y2={y(0)} className="viz-cross" /> : null}
        </svg>
        {hover !== null ? (
          <div className={`viz-tip tip-${tipAlign}`} style={{ left: tipX }} role="status">
            <strong>{points[hover]!.title}</strong>
            {bar ? (
              <span>
                <i className="key key-bar" style={{ background: bar.color }} /> {bar.name} <b>{bar.values[hover] === null ? '—' : format(bar.values[hover]!)}</b>
              </span>
            ) : null}
            {line ? (
              <span>
                <i className="key key-line" style={{ background: line.color }} /> {line.name} <b>{line.values[hover] === null ? '—' : format(line.values[hover]!)}</b>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {/* in a clipped box: a table does not shrink to the 1 px of .sr-only and widened the page on a phone */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <tbody>
            {points.map((p, i) => (
              <tr key={p.key}>
                <th scope="row">{p.title}</th>
                {bar ? <td>{bar.name}: {bar.values[i] === null ? '—' : format(bar.values[i]!)}</td> : null}
                {line ? <td>{line.name}: {line.values[i] === null ? '—' : format(line.values[i]!)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

export type BarRow = { key: string; label: string; value: number; display: string; sub?: ReactNode; href?: string; color?: string };

/** Ranked horizontal bars: the name, a bar from one baseline, the value at its tip. One series, one color. */
export function BarList({ label, rows, color = 'var(--viz-1)', empty }: { label: string; rows: BarRow[]; color?: string; empty?: ReactNode }) {
  const max = Math.max(0, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="muted">{empty}</p>;
  return (
    <ul className="viz-bars" aria-label={label}>
      {rows.map((r) => (
        <li key={r.key} title={`${r.label}: ${r.display}`}>
          <span className="viz-bars-name">{r.href ? <Link to={r.href}>{r.label}</Link> : r.label}{r.sub ? <span className="cell-sub">{r.sub}</span> : null}</span>
          <span className="viz-bars-track">
            <span className="viz-bars-bar" style={{ width: `${max > 0 ? Math.max(0.5, (Math.max(0, r.value) / max) * 100) : 0}%`, background: r.color ?? color }} />
            <span className="viz-bars-value">{r.display}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export type Slice = { key: string; label: string; value: number; color: string; href?: string };

/** Part of a whole at a glance: a donut of at most six slices with a 2px gap between them, the total in the middle, and a legend with each value and share. */
export function Donut({ label, slices, format, centerLabel, otherLabel = 'Other' }: { label: string; slices: Slice[]; format: (v: number) => string; centerLabel: string; otherLabel?: string }) {
  const [hover, setHover] = useState<string | null>(null);
  const positive = slices.filter((s) => s.value > 0);
  const parts = positive.length > 6 ? [...positive.slice(0, 5), { key: 'rest', label: otherLabel, value: positive.slice(5).reduce((t, s) => t + s.value, 0), color: 'var(--viz-other)' }] : positive;
  const total = parts.reduce((t, s) => t + s.value, 0);
  const size = 168;
  const r = 70;
  const stroke = 22;
  const c = 2 * Math.PI * r;
  const gap = parts.length > 1 ? 2 : 0;
  let at = 0;
  const shown = hover ? parts.find((p) => p.key === hover) : null;
  return (
    <figure className="viz viz-donut" aria-label={label}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {parts.map((s) => {
            const len = total > 0 ? (s.value / total) * c : 0;
            const dash = Math.max(0, len - gap);
            const el = (
              <circle
                key={s.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={hover === s.key ? stroke + 4 : stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-at}
                opacity={hover && hover !== s.key ? 0.55 : 1}
                onMouseEnter={() => setHover(s.key)}
                onMouseLeave={() => setHover(null)}
              />
            );
            at += len;
            return el;
          })}
        </g>
        <text x={size / 2} y={size / 2 - 4} textAnchor="middle" className="viz-donut-value">{format(shown ? shown.value : total)}</text>
        <text x={size / 2} y={size / 2 + 14} textAnchor="middle" className="viz-tick">{shown ? shown.label : centerLabel}</text>
      </svg>
      <ul className="viz-keys">
        {parts.map((s) => (
          <li key={s.key} onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)}>
            <i className="key key-dot" style={{ background: s.color }} />
            <span className="viz-keys-name">{s.href ? <Link to={s.href}>{s.label}</Link> : s.label}</span>
            <span className="num">{format(s.value)}</span>
            <span className="num muted">{total > 0 ? `${Math.round((s.value / total) * 1000) / 10}%` : ''}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

/** A row's trend in a table cell: a thin line over the period's buckets, the last point marked. */
export function Sparkline({ values, label, width = 96, height = 22 }: { values: number[]; label: string; width?: number; height?: number }) {
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const x = (i: number) => 2 + (i / (values.length - 1)) * (width - 4);
  const y = (v: number) => 2 + (height - 4) * (1 - (v - min) / span);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const last = values.length - 1;
  return (
    <svg className="spark" width={width} height={height} role="img" aria-label={label}>
      <path d={d} fill="none" stroke="var(--viz-1)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(last)} cy={y(values[last]!)} r={2.5} fill="var(--viz-1)" />
    </svg>
  );
}
